/**
 * Track app installations and logins for team members
 * Monitor Kyle, Carlos, Jorge installation status
 */

const TEAM_MEMBERS = [
  { name: 'Kyle', email: 'kyle@example.com', phone: '+1-555-0101' },
  { name: 'Carlos', email: 'carlos@example.com', phone: '+1-555-0102' },
  { name: 'Jorge', email: 'jorge@example.com', phone: '+1-555-0103' },
];

const TRACKING_API = 'https://property-lookup-api.anassk.workers.dev';

/**
 * Log installation attempt
 */
export async function logInstallationAttempt(teamMemberName, timestamp = new Date()) {
  const member = TEAM_MEMBERS.find(m => m.name === teamMemberName);
  if (!member) return;

  try {
    await fetch(`${TRACKING_API}/track/installation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        teamMember: teamMemberName,
        email: member.email,
        appVersion: '1.3',
        status: 'attempted',
        timestamp: timestamp.toISOString(),
        source: 'testflight',
      }),
    });
  } catch (err) {
    console.error('Failed to log installation attempt:', err);
  }
}

/**
 * Log successful installation (app opened first time)
 */
export async function logInstallationSuccess(teamMemberName, deviceInfo = {}) {
  const member = TEAM_MEMBERS.find(m => m.name === teamMemberName);
  if (!member) return;

  try {
    await fetch(`${TRACKING_API}/track/installation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        teamMember: teamMemberName,
        email: member.email,
        appVersion: '1.3',
        status: 'installed',
        timestamp: new Date().toISOString(),
        deviceInfo: {
          model: deviceInfo.model || 'unknown',
          osVersion: deviceInfo.osVersion || 'unknown',
          ...deviceInfo,
        },
        source: 'testflight',
      }),
    });
  } catch (err) {
    console.error('Failed to log installation success:', err);
  }
}

/**
 * Log login event
 */
export async function logLogin(teamMemberName, userId = null) {
  const member = TEAM_MEMBERS.find(m => m.name === teamMemberName);
  if (!member) return;

  try {
    await fetch(`${TRACKING_API}/track/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        teamMember: teamMemberName,
        email: member.email,
        userId: userId || teamMemberName,
        appVersion: '1.3',
        timestamp: new Date().toISOString(),
        loginType: 'mobile',
      }),
    });
  } catch (err) {
    console.error('Failed to log login:', err);
  }
}

/**
 * Get installation status for all team members
 */
export async function getInstallationStatus() {
  try {
    const response = await fetch(`${TRACKING_API}/track/installation-status`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error('Failed to fetch installation status:', err);
    return null;
  }
}

/**
 * Generate installation guide HTML email
 */
export function generateInstallationGuideEmail() {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #0096c7, #0076a8); color: white; padding: 30px; border-radius: 8px; text-align: center; }
    .section { margin: 20px 0; padding: 20px; background: #f9f9f9; border-radius: 8px; }
    .step { margin: 15px 0; padding: 15px; background: white; border-left: 4px solid #0096c7; }
    .step-number { background: #0096c7; color: white; width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 10px; }
    .step h3 { margin: 0; display: inline; }
    .button { display: inline-block; background: #0096c7; color: white; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: bold; margin: 10px 0; }
    .footer { text-align: center; color: #999; font-size: 12px; margin-top: 30px; }
    .emoji { font-size: 24px; margin-right: 10px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🚀 New RoofLeads v1.3 Ready!</h1>
      <p>Install in 3 minutes - New storm alerts + push notifications</p>
    </div>

    <div class="section">
      <h2>⚡ What's New:</h2>
      <ul>
        <li>📍 Real-time NOAA storm alerts (hail/wind)</li>
        <li>📬 Daily plan reminders at 8 AM</li>
        <li>⛰️ Big roof discovery notifications</li>
        <li>💰 Wealthy neighborhood alerts</li>
        <li>📊 Daily conversion tracker</li>
        <li>🗺️ Apple Maps routing + Street View fallback</li>
      </ul>
    </div>

    <div class="section">
      <h2>📱 Installation Steps (Super Simple):</h2>

      <div class="step">
        <span class="step-number">1</span>
        <h3>Open TestFlight App</h3>
        <p>On your iPhone, open the <strong>TestFlight</strong> app (the blue one)</p>
      </div>

      <div class="step">
        <span class="step-number">2</span>
        <h3>Find RoofLeads</h3>
        <p>Scroll to find <strong>"RoofLeads"</strong> in your app list</p>
      </div>

      <div class="step">
        <span class="step-number">3</span>
        <h3>Tap Install</h3>
        <p>Hit the big blue <strong>"Install"</strong> button</p>
        <p style="color: #999; font-size: 12px;">⏳ If it says "processing" - wait 5 min and refresh</p>
      </div>

      <div class="step">
        <span class="step-number">4</span>
        <h3>Enable Notifications</h3>
        <p>When it asks for notifications → tap <strong>"Allow"</strong></p>
        <p style="color: #d62828; font-weight: bold;">⚠️ IMPORTANT: Need notifications for storm alerts!</p>
      </div>

      <div class="step">
        <span class="step-number">5</span>
        <h3>Open & Login</h3>
        <p>Open RoofLeads → login with your account</p>
        <p style="color: #999; font-size: 12px;">We'll track when you're in 👀</p>
      </div>
    </div>

    <div class="section" style="background: #fff8e1; border-left: 4px solid #f77f00;">
      <h3 style="margin-top: 0;">🆘 Stuck? Try This:</h3>
      <ul>
        <li>Force-close TestFlight (swipe up)</li>
        <li>Reopen TestFlight</li>
        <li>Pull down to refresh</li>
        <li>If still pending → wait 15 min (Apple is slow)</li>
      </ul>
    </div>

    <div class="section" style="background: #e8f5e9;">
      <h3 style="margin-top: 0; color: #2e7d32;">✅ You're In!</h3>
      <p><strong>Once installed:</strong></p>
      <ul>
        <li>🚗 New storm alerts go straight to your phone</li>
        <li>📋 Daily plan reminder at 8 AM</li>
        <li>💸 See your conversion score in real-time</li>
        <li>📍 Storm zones highlighted on your map</li>
      </ul>
      <p style="margin-top: 20px;"><strong>Questions?</strong> Reply to this email or check the in-app help</p>
    </div>

    <div class="footer">
      <p>RoofLeads v1.3 | TestFlight Build</p>
      <p>📬 Push Notifications Enabled = Storm Alerts in Real-Time</p>
    </div>
  </div>
</body>
</html>
  `;
}

/**
 * Generate SMS-friendly installation guide
 */
export function generateInstallationSMS() {
  return `🚀 RoofLeads v1.3 is ready!

5 STEPS TO INSTALL:
1️⃣ Open TestFlight app
2️⃣ Find "RoofLeads"
3️⃣ Tap "Install"
4️⃣ Allow notifications ⚠️
5️⃣ Login & you're in!

⚡ New: Storm alerts, daily reminders, conversion tracker

Need help? Reply here or check email for full guide`;
}

/**
 * Dashboard to track team installation status
 */
export function getInstallationDashboard(installationData) {
  return {
    summary: {
      total: TEAM_MEMBERS.length,
      installed: installationData?.installed?.length || 0,
      loggedIn: installationData?.loggedIn?.length || 0,
      pending: TEAM_MEMBERS.length - (installationData?.installed?.length || 0),
    },
    teamStatus: TEAM_MEMBERS.map(member => {
      const installed = installationData?.installed?.some(m => m.name === member.name);
      const loggedIn = installationData?.loggedIn?.some(m => m.name === member.name);

      return {
        name: member.name,
        email: member.email,
        installed: installed ? '✅' : '⏳',
        loggedIn: loggedIn ? '✅' : '❌',
        lastLogin: installationData?.logins?.find(m => m.name === member.name)?.timestamp || null,
        installTime: installationData?.installed?.find(m => m.name === member.name)?.timestamp || null,
      };
    }),
    completionRate: {
      installation: (installationData?.installed?.length || 0) / TEAM_MEMBERS.length * 100,
      loginRate: (installationData?.loggedIn?.length || 0) / TEAM_MEMBERS.length * 100,
    },
  };
}
